import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Store, Plus, Users, Package, ShoppingCart, BarChart3, LogIn, Trash2, Sparkles, Settings, ShieldCheck, User as UserIcon, Loader2, RefreshCcw, Mail, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import IntegrationStatusPanel from '../components/IntegrationStatusPanel';
import CustomersUsersPanel from '../components/CustomersUsersPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ shop_name: '', admin_name: '', admin_email: '', admin_password: '', phone: '', address: '' });
  const [credentialsModal, setCredentialsModal] = useState(null); // {admin_email, temp_password, shop_name, email_sent, is_resend}
  const [emailChangeModal, setEmailChangeModal] = useState(null); // {shop, newEmail, alsoReset}

  useEffect(() => {
    if (authLoading) return;
    if (user?.role !== 'superadmin') { navigate('/'); return; }
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [shopsRes, statsRes] = await Promise.all([
        axios.get(`${API}/superadmin/shops`, { withCredentials: true }),
        axios.get(`${API}/superadmin/stats`, { withCredentials: true })
      ]);
      setShops(shopsRes.data.shops || []);
      setStats(statsRes.data || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const createShop = async (e) => {
    e.preventDefault();
    if (!form.shop_name || !form.admin_email) {
      toast.error('Compila Nome Negozio e Email');
      return;
    }
    setCreating(true);
    try {
      // Don't send admin_password key if blank — let backend auto-generate
      const payload = { ...form };
      if (!payload.admin_password) delete payload.admin_password;
      const { data } = await axios.post(`${API}/superadmin/shops`, payload, { withCredentials: true });
      toast.success(`Negozio "${form.shop_name}" creato!`);
      setShowCreate(false);
      // Always show credentials modal so SuperAdmin can copy them
      setCredentialsModal({
        admin_email: data.admin_email,
        temp_password: data.temp_password || form.admin_password,
        shop_name: form.shop_name,
        email_sent: data.email_sent,
        email_provider: data.email_provider,
        auto_generated: data.auto_generated_password,
        is_resend: false,
      });
      setForm({ shop_name: '', admin_name: '', admin_email: '', admin_password: '', phone: '', address: '' });
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore creazione';
      toast.error(typeof msg === 'string' ? msg : 'Errore');
    } finally {
      setCreating(false);
    }
  };

  const activateDomain = async (shop) => {
    if (!window.confirm(`Attivare il dominio "${shop.custom_domain}" per "${shop.name}"?\n\n⚠️ Prima di attivare assicurati di aver:\n1. Aggiunto il dominio nella dashboard Emergent (Domains)\n2. Verificato che il CNAME punti correttamente\n3. Attivato l'SSL (Let's Encrypt)\n\nIl negoziante riceverà una email di conferma.`)) return;
    try {
      await axios.post(`${API}/superadmin/shops/${shop.id}/activate-domain`, {}, { withCredentials: true });
      toast.success(`Dominio ${shop.custom_domain} attivato`);
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore';
      toast.error(typeof msg === 'string' ? msg : 'Errore');
    }
  };

  const toggleCustomDomainAddon = async (shop) => {
    const isOn = (shop.addons || []).includes('custom_domain');
    const action = isOn ? 'DISATTIVARE' : 'ATTIVARE';
    if (!window.confirm(`${action} l'addon "Dominio Personalizzato" (€5/mese) per "${shop.name}"?`)) return;
    try {
      await axios.post(`${API}/superadmin/shops/${shop.id}/toggle-addon`, {
        addon_id: 'custom_domain',
        enabled: !isOn,
      }, { withCredentials: true });
      toast.success(isOn ? 'Addon disattivato' : 'Addon attivato (€5/mese)');
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore';
      toast.error(typeof msg === 'string' ? msg : 'Errore');
    }
  };

  const submitEmailChange = async () => {
    if (!emailChangeModal) return;
    const { shop, newEmail, alsoReset } = emailChangeModal;
    try {
      const { data } = await axios.post(`${API}/superadmin/shops/${shop.id}/change-admin-email`, {
        new_email: newEmail.trim().toLowerCase(),
        also_reset_password: alsoReset,
      }, { withCredentials: true });
      toast.success(data.message || 'Email aggiornata');
      setEmailChangeModal(null);
      if (alsoReset && data.new_password) {
        setCredentialsModal({
          admin_email: data.new_email,
          temp_password: data.new_password,
          shop_name: shop.name,
          email_sent: data.email_sent,
          email_provider: data.email_provider,
          auto_generated: true,
          is_resend: true,
        });
      }
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore';
      toast.error(typeof msg === 'string' ? msg : 'Errore');
    }
  };

  const resendCredentials = async (shop) => {
    if (!window.confirm(`Rigenerare la password per il negoziante di "${shop.name}"?\nLa password attuale non sarà più valida.`)) return;
    try {
      const { data } = await axios.post(`${API}/superadmin/shops/${shop.id}/resend-credentials`, {}, { withCredentials: true });
      setCredentialsModal({
        admin_email: data.admin_email,
        temp_password: data.temp_password,
        shop_name: shop.name,
        email_sent: data.email_sent,
        email_provider: data.email_provider,
        auto_generated: true,
        is_resend: true,
      });
      toast.success('Credenziali rigenerate');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore';
      toast.error(typeof msg === 'string' ? msg : 'Errore');
    }
  };

  const enterShop = (shopId) => {
    localStorage.setItem('phv_active_shop', shopId);
    toast.success('Entrato nel negozio');
    navigate('/admin');
  };

  const deactivateShop = async (shopId) => {
    if (!window.confirm('Disattivare questo negozio?')) return;
    try {
      await axios.delete(`${API}/superadmin/shops/${shopId}`, { withCredentials: true });
      toast.success('Negozio disattivato');
      fetchData();
    } catch (e) {
      toast.error('Errore');
    }
  };

  if (user?.role !== 'superadmin') return null;

  return (
    <div data-testid="superadmin-page" className="pb-safe px-4 pt-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            SuperAdmin Panel
          </h1>
          <p className="text-sm text-[#877E78]">Gestione piattaforma PhotoVendo</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Negozi', value: stats.total_shops || 0, icon: Store, color: '#C25934' },
          { label: 'Prodotti', value: stats.total_products || 0, icon: Package, color: '#4A6B53' },
          { label: 'Ordini', value: stats.total_orders || 0, icon: ShoppingCart, color: '#D98324' },
          { label: 'Clienti', value: stats.total_users || 0, icon: Users, color: '#0088cc' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-[#E8E3D9] p-3">
            <s.icon size={20} style={{ color: s.color }} />
            <p className="text-2xl font-bold text-[#2A2624] mt-1" style={{ fontFamily: 'Outfit' }}>{s.value}</p>
            <p className="text-xs text-[#877E78]">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Quick access: Test Profiles for SuperAdmin */}
      <QuickTestProfiles />

      {/* Integration health overview */}
      <div className="mt-3">
        <IntegrationStatusPanel />
      </div>

      {/* Clienti per negozio + lista utenti con reset password */}
      <div className="mt-3">
        <CustomersUsersPanel />
      </div>

      {/* Create Shop Button */}
      <Button
        data-testid="go-test-profiles-btn"
        onClick={() => navigate('/superadmin/test-profiles')}
        variant="outline"
        className="w-full h-11 rounded-xl border-[#C25934]/40 text-[#C25934] hover:bg-[#C25934]/5 mb-2"
      >
        🧪 Gestisci tutti i Profili Test
      </Button>
      <Button
        data-testid="go-platform-settings-btn"
        onClick={() => navigate('/superadmin/impostazioni')}
        variant="outline"
        className="w-full h-11 rounded-xl border-[#4A6B53]/40 text-[#4A6B53] hover:bg-[#4A6B53]/5 mb-3"
      >
        <Settings size={16} className="mr-2" /> Impostazioni Piattaforma · Piani AI · Chiavi
      </Button>
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogTrigger asChild>
          <Button
            data-testid="create-shop-btn"
            className="w-full h-12 rounded-xl bg-[#C25934] hover:bg-[#A64B2B] text-white mb-4"
          >
            <Plus size={18} className="mr-2" /> Crea Nuovo Negozio
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Crea Nuovo Negozio</DialogTitle>
          </DialogHeader>
          <form onSubmit={createShop} className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#5C554F]">Nome Negozio *</label>
              <Input
                data-testid="new-shop-name"
                value={form.shop_name}
                onChange={e => setForm(p => ({ ...p, shop_name: e.target.value }))}
                className="h-12 rounded-xl border-[#E8E3D9] mt-1"
                placeholder="Es: Casa & Stile"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5C554F]">Nome Negoziante *</label>
              <Input
                data-testid="new-admin-name"
                value={form.admin_name}
                onChange={e => setForm(p => ({ ...p, admin_name: e.target.value }))}
                className="h-12 rounded-xl border-[#E8E3D9] mt-1"
                placeholder="Mario Rossi"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5C554F]">Email Negoziante *</label>
              <Input
                data-testid="new-admin-email"
                type="email"
                value={form.admin_email}
                onChange={e => setForm(p => ({ ...p, admin_email: e.target.value }))}
                className="h-12 rounded-xl border-[#E8E3D9] mt-1"
                placeholder="negoziante@email.com"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5C554F]">
                Password <span className="text-[#877E78] font-normal">(opzionale — se vuota verrà generata automaticamente)</span>
              </label>
              <Input
                data-testid="new-admin-password"
                type="text"
                value={form.admin_password}
                onChange={e => setForm(p => ({ ...p, admin_password: e.target.value }))}
                className="h-12 rounded-xl border-[#E8E3D9] mt-1 font-mono"
                placeholder="Lascia vuoto per auto-generare"
              />
              <p className="text-[11px] text-[#877E78] mt-1">📧 Le credenziali verranno inviate via email al negoziante.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-[#5C554F]">Telefono</label>
                <Input
                  value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  className="h-12 rounded-xl border-[#E8E3D9] mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#5C554F]">Indirizzo</label>
                <Input
                  value={form.address}
                  onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                  className="h-12 rounded-xl border-[#E8E3D9] mt-1"
                />
              </div>
            </div>
            <Button
              data-testid="submit-create-shop"
              type="submit"
              disabled={creating}
              className="w-full h-12 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white"
            >
              {creating ? 'Creazione...' : 'Crea Negozio'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Shops List */}
      <h2 className="text-lg font-medium text-[#2A2624] mb-3" style={{ fontFamily: 'Outfit' }}>
        Negozi ({shops.length})
      </h2>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-24 skeleton-shimmer rounded-xl" />)}
        </div>
      ) : shops.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-[#E8E3D9]">
          <Store size={40} className="mx-auto text-[#877E78] mb-3" />
          <p className="text-[#877E78]">Nessun negozio ancora. Creane uno!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {shops.map(shop => (
            <div key={shop.id} data-testid={`shop-card-${shop.id}`} className="bg-white rounded-xl border border-[#E8E3D9] p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                    {shop.name || 'Senza nome'}
                  </h3>
                  <p className="text-xs text-[#877E78]">{shop.admin?.email || shop.email}</p>
                  {shop.custom_domain && (
                    <div className="mt-1 flex items-center gap-1.5 text-[11px] font-mono">
                      <span className="text-[#5C554F]">🌐 {shop.custom_domain}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${shop.custom_domain_status === 'active' ? 'bg-[#4A6B53]/15 text-[#4A6B53]' : 'bg-amber-100 text-amber-800'}`}>
                        {shop.custom_domain_status === 'active' ? 'ATTIVO' : 'PENDING'}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-3 mt-2 text-xs text-[#5C554F]">
                    <span><Package size={12} className="inline mr-1" />{shop.product_count} prodotti</span>
                    <span><ShoppingCart size={12} className="inline mr-1" />{shop.order_count} ordini</span>
                  </div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  shop.active !== false ? 'bg-[#4A6B53]/10 text-[#4A6B53]' : 'bg-red-100 text-red-600'
                }`}>
                  {shop.active !== false ? 'Attivo' : 'Disattivato'}
                </span>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button
                  data-testid={`enter-shop-${shop.id}`}
                  onClick={() => enterShop(shop.id)}
                  size="sm"
                  className="bg-[#C25934] hover:bg-[#A64B2B] text-white rounded-lg text-xs h-8"
                >
                  <LogIn size={14} className="mr-1" /> Entra
                </Button>
                <Button
                  data-testid={`view-orders-${shop.id}`}
                  onClick={() => { localStorage.setItem('phv_active_shop', shop.id); navigate('/ordini'); }}
                  size="sm"
                  variant="outline"
                  className="border-[#E8E3D9] text-[#5C554F] rounded-lg text-xs h-8"
                >
                  <Package size={14} className="mr-1" /> Ordini
                </Button>
                <Button
                  data-testid={`settings-shop-${shop.id}`}
                  onClick={() => { localStorage.setItem('phv_active_shop', shop.id); navigate('/negozio/impostazioni'); }}
                  size="sm"
                  variant="outline"
                  className="border-[#E8E3D9] text-[#5C554F] rounded-lg text-xs h-8"
                >
                  <Store size={14} className="mr-1" /> Impostazioni
                </Button>
                <Button
                  data-testid={`resend-credentials-${shop.id}`}
                  onClick={() => resendCredentials(shop)}
                  size="sm"
                  variant="outline"
                  className="border-[#C25934]/30 text-[#C25934] hover:bg-[#C25934]/10 rounded-lg text-xs h-8"
                  title="Rigenera password e re-invia email al negoziante"
                >
                  <Mail size={14} className="mr-1" /> Credenziali
                </Button>
                <Button
                  data-testid={`change-email-${shop.id}`}
                  onClick={() => setEmailChangeModal({ shop, newEmail: '', alsoReset: false })}
                  size="sm"
                  variant="outline"
                  className="border-[#5C8C8E]/40 text-[#5C8C8E] hover:bg-[#5C8C8E]/10 rounded-lg text-xs h-8"
                  title="Cambia l'email di accesso del negoziante"
                >
                  ✉️ Cambia email
                </Button>
                {shop.custom_domain && shop.custom_domain_status !== 'active' && (
                  <Button
                    data-testid={`activate-domain-${shop.id}`}
                    onClick={() => activateDomain(shop)}
                    size="sm"
                    variant="outline"
                    className="border-[#4A6B53]/30 text-[#4A6B53] hover:bg-[#4A6B53]/10 rounded-lg text-xs h-8"
                    title="Attiva il custom domain (dopo aver verificato il CNAME e configurato il dominio su Emergent)"
                  >
                    🌐 Attiva dominio
                  </Button>
                )}
                <Button
                  data-testid={`toggle-domain-addon-${shop.id}`}
                  onClick={() => toggleCustomDomainAddon(shop)}
                  size="sm"
                  variant="outline"
                  className={`rounded-lg text-xs h-8 ${
                    (shop.addons || []).includes('custom_domain')
                      ? 'border-[#4A6B53]/40 text-[#4A6B53] bg-[#4A6B53]/10 hover:bg-[#4A6B53]/20'
                      : 'border-[#E8E3D9] text-[#5C554F] hover:bg-[#FFF6F0]'
                  }`}
                  title="Attiva/disattiva l'addon Dominio Personalizzato (€5/mese)"
                >
                  {(shop.addons || []).includes('custom_domain') ? '✓ Addon Dominio' : '+ Addon Dominio €5'}
                </Button>
                <Button
                  data-testid={`deactivate-shop-${shop.id}`}
                  onClick={() => deactivateShop(shop.id)}
                  size="sm"
                  variant="outline"
                  className="ml-auto border-red-200 text-red-500 hover:bg-red-50 rounded-lg text-xs h-8"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
              <ShopAiPlanSection shop={shop} onRefresh={fetchData} />
            </div>
          ))}
        </div>
      )}

      {/* Credentials modal */}
      <Dialog open={!!credentialsModal} onOpenChange={(o) => !o && setCredentialsModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              {credentialsModal?.is_resend ? '🔄 Credenziali rigenerate' : '✅ Negozio creato'}
            </DialogTitle>
          </DialogHeader>
          {credentialsModal && (
            <div className="space-y-3">
              <div className={`rounded-xl p-3 ${credentialsModal.email_sent ? 'bg-[#4A6B53]/10 border border-[#4A6B53]/30' : 'bg-amber-50 border border-amber-200'}`}>
                <p className="text-sm font-semibold text-[#2A2624]">
                  {credentialsModal.email_sent
                    ? `📧 Email inviata via ${credentialsModal.email_provider || 'SMTP'}`
                    : `⚠️ Email NON inviata (provider: ${credentialsModal.email_provider || 'simulato'})`}
                </p>
                <p className="text-xs text-[#5C554F] mt-1">
                  {credentialsModal.email_sent
                    ? `Le credenziali sono state inviate a ${credentialsModal.admin_email}. Comunque, copia anche qui sotto per sicurezza.`
                    : 'Configura SMTP/Resend nel file .env per inviare email reali. Per ora copia manualmente le credenziali sotto e inviale al negoziante.'}
                </p>
              </div>

              <div className="bg-[#F8F5F0] border border-[#E8E3D9] rounded-xl p-3 space-y-2">
                <div>
                  <div className="text-[10px] font-bold text-[#877E78] uppercase">Email</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <code data-testid="cred-modal-email" className="flex-1 font-mono text-sm text-[#2A2624] bg-white px-2 py-1.5 rounded border border-[#E8E3D9]">{credentialsModal.admin_email}</code>
                    <Button
                      data-testid="cred-modal-copy-email"
                      onClick={() => { navigator.clipboard.writeText(credentialsModal.admin_email); toast.success('Email copiata'); }}
                      size="sm"
                      variant="outline"
                      className="h-9 px-2 border-[#E8E3D9]"
                    >
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-[#877E78] uppercase">
                    Password {credentialsModal.auto_generated ? 'provvisoria' : ''}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <code data-testid="cred-modal-password" className="flex-1 font-mono text-sm text-[#C25934] bg-white px-2 py-1.5 rounded border border-dashed border-[#C25934]">{credentialsModal.temp_password}</code>
                    <Button
                      data-testid="cred-modal-copy-password"
                      onClick={() => { navigator.clipboard.writeText(credentialsModal.temp_password); toast.success('Password copiata'); }}
                      size="sm"
                      variant="outline"
                      className="h-9 px-2 border-[#E8E3D9]"
                    >
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
              </div>

              {credentialsModal.auto_generated && (
                <p className="text-[11px] text-[#877E78] italic">
                  💡 Il negoziante riceverà l'invito a cambiare la password al primo accesso.
                </p>
              )}

              <Button
                data-testid="cred-modal-close"
                onClick={() => setCredentialsModal(null)}
                className="w-full h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white"
              >
                Chiudi
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Admin Email modal */}
      <Dialog open={!!emailChangeModal} onOpenChange={(o) => !o && setEmailChangeModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>✉️ Cambia email negoziante</DialogTitle>
          </DialogHeader>
          {emailChangeModal && (
            <div className="space-y-4">
              <div className="text-xs text-[#5C554F] bg-[#F8F5F0] rounded-xl p-3 border border-[#E8E3D9]">
                <div><b>Negozio:</b> {emailChangeModal.shop.name}</div>
                <div><b>Email attuale:</b> <span className="font-mono text-[#877E78]">{emailChangeModal.shop.admin?.email || emailChangeModal.shop.email}</span></div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#5C554F] uppercase mb-1 block">Nuova email *</label>
                <Input
                  data-testid="change-email-new-input"
                  type="email"
                  value={emailChangeModal.newEmail}
                  onChange={e => setEmailChangeModal(p => ({ ...p, newEmail: e.target.value }))}
                  placeholder="es. mario.rossi@gmail.com"
                  className="h-11 rounded-xl border-[#E8E3D9] font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-[#5C554F] uppercase">Scegli azione</label>
                <label data-testid="change-email-keep-pw" className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer ${!emailChangeModal.alsoReset ? 'bg-[#C25934]/5 border-[#C25934]/30' : 'border-[#E8E3D9] hover:bg-[#F8F5F0]'}`}>
                  <input type="radio" checked={!emailChangeModal.alsoReset} onChange={() => setEmailChangeModal(p => ({ ...p, alsoReset: false }))} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#2A2624]">Solo cambia email (password invariata)</div>
                    <div className="text-[11px] text-[#877E78] mt-0.5">Il negoziante riceve un'email sulla NUOVA casella con conferma. La password attuale resta valida.</div>
                  </div>
                </label>
                <label data-testid="change-email-also-reset-pw" className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer ${emailChangeModal.alsoReset ? 'bg-[#C25934]/5 border-[#C25934]/30' : 'border-[#E8E3D9] hover:bg-[#F8F5F0]'}`}>
                  <input type="radio" checked={emailChangeModal.alsoReset} onChange={() => setEmailChangeModal(p => ({ ...p, alsoReset: true }))} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#2A2624]">Cambia email + nuova password</div>
                    <div className="text-[11px] text-[#877E78] mt-0.5">Genera anche una nuova password provvisoria. Invia email con entrambe le credenziali sulla nuova casella.</div>
                  </div>
                </label>
              </div>
              <div className="text-[11px] text-[#877E78] bg-amber-50 border border-amber-200 rounded-lg p-2">
                🔐 Per sicurezza inviamo anche un avviso sulla VECCHIA email con la notifica del cambio.
              </div>
              <div className="flex gap-2">
                <Button data-testid="change-email-cancel" variant="outline" onClick={() => setEmailChangeModal(null)} className="flex-1 h-11 rounded-full border-[#E8E3D9]">Annulla</Button>
                <Button data-testid="change-email-submit" onClick={submitEmailChange} disabled={!emailChangeModal.newEmail.trim() || !emailChangeModal.newEmail.includes('@')} className="flex-1 h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white">Conferma cambio</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShopAiPlanSection({ shop, onRefresh }) {  const [plans, setPlans] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [form, setForm] = useState({ plan_id: '', duration_days: 30, payment_provider: 'bank_transfer', payment_reference: '', amount_paid: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!showAssign) return;
    axios.get(`${API}/superadmin/platform-settings`, { withCredentials: true })
      .then(r => {
        const list = (r.data.ai_plans || []).filter(p => p.active);
        setPlans(list);
        if (list.length && !form.plan_id) {
          setForm(f => ({ ...f, plan_id: list[0].id, duration_days: list[0].duration_days || 30, amount_paid: list[0].price_monthly || 0 }));
        }
      })
      .catch(() => {});
  }, [showAssign]); // eslint-disable-line

  const assign = async () => {
    if (!form.plan_id) { toast.error('Seleziona un piano'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/superadmin/shops/${shop.id}/assign-ai-plan`, form, { withCredentials: true });
      toast.success('Piano AI assegnato');
      setShowAssign(false);
      onRefresh?.();
    } catch (e) {
      toast.error('Errore assegnazione');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Revocare il piano AI?')) return;
    try {
      await axios.post(`${API}/superadmin/shops/${shop.id}/revoke-ai-plan`, {}, { withCredentials: true });
      toast.success('Piano AI revocato');
      onRefresh?.();
    } catch (e) {
      toast.error('Errore revoca');
    }
  };

  const status = shop.ai_plan_status || 'none';
  const expires = shop.ai_plan_expires_at;
  const expired = expires && new Date(expires) < new Date();
  const tone = status === 'active' && !expired ? 'bg-[#4A6B53]/10 text-[#4A6B53]' : 'bg-amber-100 text-amber-700';

  return (
    <div className="mt-3 pt-3 border-t border-[#E8E3D9]" data-testid={`ai-plan-section-${shop.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs">
          <Sparkles size={12} className="text-[#8B5CF6]" />
          <span className="font-bold text-[#5C554F]">Piano AI:</span>
          <span className={`px-2 py-0.5 rounded-full font-semibold ${tone}`}>
            {status === 'active' && !expired
              ? `${shop.ai_plan_name || '—'} · ${shop.ai_plan_used || 0}/${shop.ai_plan_included || '?'}`
              : status === 'active' && expired ? 'Scaduto' : status === 'revoked' ? 'Revocato' : 'Non attivo'}
          </span>
          {expires && <span className="text-[#877E78]">scad. {new Date(expires).toLocaleDateString('it-IT')}</span>}
        </div>
        <div className="flex gap-1">
          <Button size="sm" onClick={() => setShowAssign(true)} data-testid={`assign-plan-${shop.id}`} className="h-7 rounded-lg bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs">
            {status === 'active' ? 'Rinnova/Cambia' : 'Assegna'}
          </Button>
          {status === 'active' && (
            <Button size="sm" onClick={revoke} variant="outline" data-testid={`revoke-plan-${shop.id}`} className="h-7 rounded-lg border-red-200 text-red-500 text-xs">
              Revoca
            </Button>
          )}
        </div>
      </div>

      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Assegna Piano AI a {shop.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-[#877E78] uppercase">Piano</label>
              <select value={form.plan_id} onChange={e => {
                const p = plans.find(x => x.id === e.target.value);
                setForm(f => ({ ...f, plan_id: e.target.value, duration_days: p?.duration_days || 30, amount_paid: p?.price_monthly || 0 }));
              }} className="w-full px-3 py-2 rounded-lg border border-[#E8E3D9] bg-white" data-testid="assign-plan-select">
                {plans.map(p => <option key={p.id} value={p.id}>{p.name} — €{p.price_monthly}/mese · {p.included_elaborations} elab</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold text-[#877E78] uppercase">Durata (gg)</label>
                <Input type="number" value={form.duration_days} onChange={e => setForm(f => ({ ...f, duration_days: parseInt(e.target.value, 10) || 30 }))} data-testid="assign-duration" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#877E78] uppercase">Importo €</label>
                <Input type="number" step="0.01" value={form.amount_paid} onChange={e => setForm(f => ({ ...f, amount_paid: parseFloat(e.target.value) || 0 }))} data-testid="assign-amount" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-[#877E78] uppercase">Metodo pagamento</label>
              <select value={form.payment_provider} onChange={e => setForm(f => ({ ...f, payment_provider: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-[#E8E3D9] bg-white" data-testid="assign-provider">
                <option value="bank_transfer">Bonifico</option>
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
                <option value="cash">Contanti</option>
                <option value="manual">Manuale / Altro</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-[#877E78] uppercase">Rif. pagamento (TRN, fattura, note)</label>
              <Input value={form.payment_reference} onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))} placeholder="es: #INV-2026-012" data-testid="assign-reference" />
            </div>
            <Button onClick={assign} disabled={saving} className="w-full h-11 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white" data-testid="confirm-assign-btn">
              {saving ? 'Assegno...' : 'Conferma assegnazione'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


const ROLE_META = {
  superadmin: { label: 'SuperAdmin', color: 'bg-[#4A6B53]/10 text-[#4A6B53]', Icon: ShieldCheck, destination: '/superadmin' },
  admin: { label: 'Admin Negozio', color: 'bg-[#C25934]/10 text-[#C25934]', Icon: Store, destination: '/admin' },
  customer: { label: 'Cliente', color: 'bg-[#8B5CF6]/10 text-[#8B5CF6]', Icon: UserIcon, destination: '/' },
};

function QuickTestProfiles() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [loginId, setLoginId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/superadmin/test-profiles`, { withCredentials: true });
      setProfiles(data.profiles || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const seed = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/superadmin/seed-test-profiles`, {}, { withCredentials: true });
      toast.success('Profili test rigenerati');
      await load();
    } catch { toast.error('Errore creazione profili'); }
    finally { setSeeding(false); }
  };

  const impersonate = async (p) => {
    setLoginId(p.id);
    try {
      const { data } = await axios.post(`${API}/superadmin/impersonate/${p.id}`, {}, { withCredentials: true });
      await refreshUser();
      toast.success(`Accesso come ${data.name || data.email}`);
      const dest = ROLE_META[data.role]?.destination || '/';
      setTimeout(() => navigate(dest), 100);
    } catch { toast.error('Errore accesso rapido'); }
    finally { setLoginId(null); }
  };

  return (
    <div data-testid="quick-test-profiles" className="bg-white rounded-2xl border border-[#E8E3D9] p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#8B5CF6]" />
          <h3 className="font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            Profili Test · Accesso Rapido
          </h3>
        </div>
        <button
          data-testid="quick-test-seed-btn"
          onClick={seed}
          disabled={seeding}
          className="text-[11px] text-[#877E78] hover:text-[#5C554F] flex items-center gap-1"
          title="Rigenera profili demo"
        >
          {seeding ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
          Rigenera
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[1,2,3,4].map(i => <div key={i} className="h-20 skeleton-shimmer rounded-xl" />)}
        </div>
      ) : profiles.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-xs text-[#877E78] mb-2">Nessun profilo trovato</p>
          <Button data-testid="quick-test-initial-seed" onClick={seed} size="sm" className="h-8 rounded-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs">
            Crea profili demo
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(expanded ? profiles : profiles.slice(0, 4)).map(p => {
              const meta = ROLE_META[p.role] || ROLE_META.customer;
              const { Icon } = meta;
              return (
                <button
                  key={p.id}
                  data-testid={`quick-login-${p.role}-${p.id}`}
                  onClick={() => impersonate(p)}
                  disabled={loginId === p.id}
                  className="group flex flex-col items-start gap-1 p-2.5 rounded-xl border border-[#E8E3D9] hover:border-[#C25934] hover:bg-[#C25934]/5 transition-colors text-left"
                >
                  <div className="flex items-center gap-1.5 w-full">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${meta.color}`}>
                      {loginId === p.id
                        ? <Loader2 size={14} className="animate-spin" />
                        : <Icon size={14} />}
                    </div>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${meta.color}`}>
                      {meta.label}
                    </span>
                  </div>
                  <p className="text-xs font-medium text-[#2A2624] truncate w-full" style={{ fontFamily: 'Outfit' }}>
                    {p.name || p.email}
                  </p>
                  {p.shop?.name && (
                    <p className="text-[10px] text-[#877E78] truncate w-full">{p.shop.name}</p>
                  )}
                  <span className="text-[10px] text-[#C25934] font-medium mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                    <LogIn size={10} /> Entra
                  </span>
                </button>
              );
            })}
          </div>
          {profiles.length > 4 && (
            <button
              data-testid="quick-test-toggle"
              onClick={() => setExpanded(v => !v)}
              className="w-full mt-2 text-[11px] text-[#877E78] hover:text-[#C25934]"
            >
              {expanded ? 'Mostra meno ↑' : `Mostra tutti i ${profiles.length} profili ↓`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
